import {
  defineNuxtPlugin,
  useHead,
  useRouter,
  useRuntimeConfig,
} from '#imports'
import { computed, ref, watch } from 'vue'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { useDataConsent } from '../composables/useDataConsent'
import { useDisableTrackers } from '../composables/useDisableTrackers'
import { getDidomiScriptContent } from '../didomi/script'

export function initDidomiScript() {
  const { currentRoute } = useRouter()
  const {
    public: { tracking },
    app,
  } = useRuntimeConfig()
  const { market } = useMarketplace()
  const disabledTrackers = useDisableTrackers()
  const { isCookiesModalDisplayed } = useDataConsent()

  const hasInteracted = ref(false)

  const isDelayedPage = ['landing_page', 'universe_page'].includes(
    String(currentRoute.value.name),
  )

  const appendDidomiScript = computed(() => {
    const noticeIdForCountry = tracking.didomi.noticeId[market.countryCode]
    const hideCookieModal = !!currentRoute.value.meta?.noCookieModal
    const hasImplicitConsent = tracking.countriesWithNoCookieOptIn.includes(
      market.countryCode,
    )
    const hasNoticeId = !!noticeIdForCountry && noticeIdForCountry !== 'unknown'
    const isDidomiEnabled = hasNoticeId && !disabledTrackers

    if (!isDidomiEnabled || hideCookieModal) return false
    if (hasImplicitConsent) return true
    if (isDelayedPage) return hasInteracted.value

    return true
  })

  // useHead is reactive to the hasInteracted ref for PLP page
  // specific loading behavior
  useHead(() => {
    if (!appendDidomiScript.value) return undefined

    return {
      script: [
        {
          innerHTML: getDidomiScriptContent({
            config: tracking,
            countryCode: market.countryCode,
            cdnURL: app?.cdnURL,
          }),
          type: 'text/javascript',
          tagPriority: 'critical',
        },
      ],
    }
  })

  // changes to the didomi script loading on PLP pages
  // only happen with client side dom interactions.
  if (import.meta.client) {
    watch([isCookiesModalDisplayed, appendDidomiScript], () => {
      if (!isCookiesModalDisplayed.value || !appendDidomiScript.value) {
        return
      }

      // "isCookiesModalDisplayed" is true if "DIDOMI_COOKIE_NAME" is absent.
      // If it's absent, we reset the config setup and display the banner once again
      // this can happen if the user clears the browser cookie data.
      window?.didomiOnReady?.push(() => {
        window?.Didomi?.reset?.()
        window?.Didomi?.notice?.show?.()
      })
    })

    function handleInteraction() {
      if (hasInteracted.value) return

      hasInteracted.value = true
    }

    if (isDelayedPage && document) {
      document.addEventListener('scroll', handleInteraction, { once: true })
      document.addEventListener('click', handleInteraction, { once: true })
      document.addEventListener('keyup', handleInteraction, { once: true })
      document.addEventListener('touchend', handleInteraction, { once: true })
      document.addEventListener('touchcancel', handleInteraction, {
        once: true,
      })
    }
  }
}

export default defineNuxtPlugin({
  name: 'initDidomiScript',
  enforce: 'pre',
  setup: initDidomiScript,
})
